import { css } from '@emotion/css';
import { CSSInterpolation } from '@emotion/css/create-instance';

import { Maybe } from 'dg-web-shared/lib/MaybeV2.ts';
import { IconButton24pxType } from '../buttons/IconButton.tsx';
import { UiContext } from '../Context.ts';
import {
    ActionButton,
    ActionButtons,
    ActionButtonsContainer,
    BelowContent,
    Content,
    Label,
    LabeledElement,
} from './Internal.tsx';
import React, { type JSX } from 'react';

export enum TextFieldIcon {
    info,
}

interface Props {
    value: string;
    label: React.ReactNode;
    inputType: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (v: any) => void;
    saveable?: boolean;
    onBlur?: () => void;
    onSave?: () => void;
    onCancel?: () => void;
    disabled?: boolean;
    errorText?: React.ReactNode;
    tabIndex?: Maybe<number>;
    maxLength?: Maybe<number>;
    multiline?: Maybe<boolean>;
    context?: UiContext;
    autofocus?: boolean;
    min?: number;
    max?: number;
    step?: number;
    toolTip?: React.ReactNode;
    toolTipRight?: boolean;
}

export function TextField(props: Props) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

    const [focused, setFocused] = React.useState(false);
    const [hovered, setHovered] = React.useState(false);

    function focusInputField(): void {
        getActiveElement()?.focus();
    }

    // Caret position captured while typing, so it can be restored after the
    // value comes back with automatic modifications applied. This replaces the
    // old UNSAFE_componentWillUpdate, which could inspect the DOM before React
    // wrote the new value; an effect runs after that write, so the pre-change
    // length and caret index have to be recorded here instead.
    const pendingCaret = React.useRef<{ length: number; index: number } | null>(
        null,
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function _onChange(e: any): void {
        const node = e.target;
        pendingCaret.current = {
            length: node.value.length,
            index: node.selectionStart ?? 0,
        };
        props.onChange(node.value);
    }

    React.useLayoutEffect(() => {
        const pending = pendingCaret.current;
        pendingCaret.current = null;
        if (!pending) {
            return;
        }
        const node = getActiveElement();
        if (!node || props.value === undefined || props.value === null) {
            return;
        }
        const newIdx = Math.max(
            0,
            node.value.length - pending.length + pending.index,
        );
        node.selectionStart = newIdx;
        node.selectionEnd = newIdx;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value]);

    // Was UNSAFE_componentWillReceiveProps: clear the hover state when the
    // field stops being saveable, so the action buttons cannot stay lit.
    React.useEffect(() => {
        if (!props.saveable && hovered) {
            setHovered(false);
        }
    }, [props.saveable, hovered]);

    React.useEffect(() => {
        if (props.autofocus === true && !props.disabled) {
            focusInputField();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function renderSaveCancelIcons(): JSX.Element | null {
        if (props.saveable) {
            return (
                <ActionButtonsContainer>
                    <ActionButton
                        type={IconButton24pxType.apply}
                        onMouseOver={() => setHovered(true)}
                        onMouseOut={() => setHovered(false)}
                        onClick={props.onSave || (() => {})}
                    />
                    <ActionButton
                        type={IconButton24pxType.clear}
                        onMouseOver={() => setHovered(true)}
                        onMouseOut={() => setHovered(false)}
                        onClick={props.onCancel || (() => {})}
                    />
                </ActionButtonsContainer>
            );
        } else {
            return null;
        }
    }

    function renderInput(): JSX.Element {
        const styles: CSSInterpolation = {
            margin: 0,
            padding: 0,
            border: 'none',
            outline: 'none',
            verticalAlign: 'baseline',
            whiteSpace: 'normal',
            background: 'none',
            lineHeight: 'inherit',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            color: 'inherit',
            fontWeight: 'inherit',
            display: 'block',
            width: '100%',
            '&:focus': {
                outline: 0,
            },
        };

        if (props.multiline) {
            return (
                <textarea
                    className={css(styles, { height: 96 })}
                    ref={textAreaRef}
                    value={props.value}
                    onFocus={(): void => setFocused(true)}
                    onBlur={(): void => {
                        setFocused(false);

                        if (props.onBlur) {
                            props.onBlur();
                        }
                    }}
                    onChange={(
                        e: React.SyntheticEvent<HTMLTextAreaElement>,
                    ): void => _onChange(e)}
                    disabled={props.disabled}
                    tabIndex={props.tabIndex || undefined}
                    maxLength={props.maxLength || undefined}
                />
            );
        } else {
            return (
                <input
                    className={css(styles)}
                    ref={inputRef}
                    value={props.value}
                    type={props.inputType}
                    onFocus={(): void => setFocused(true)}
                    onBlur={(): void => {
                        setFocused(false);

                        if (props.onBlur) {
                            props.onBlur();
                        }
                    }}
                    onChange={(
                        e: React.SyntheticEvent<HTMLInputElement>,
                    ): void => _onChange(e)}
                    disabled={props.disabled}
                    tabIndex={props.tabIndex || undefined}
                    maxLength={props.maxLength || undefined}
                    min={props.min}
                    max={props.max}
                    step={props.step}
                />
            );
        }
    }

    function getActiveElement(): HTMLInputElement | HTMLTextAreaElement | null {
        return props.multiline ? textAreaRef.current : inputRef.current;
    }

    return (
        <LabeledElement
            actionButtons={
                props.saveable ? ActionButtons.two : ActionButtons.none
            }
            onMouseOver={() => setHovered(true)}
            onMouseOut={() => setHovered(false)}
        >
            <Label
                focused={focused || false}
                hovered={false}
                label={props.label}
                context={props.context}
                toolTip={props.toolTip}
                toolTipRight={props.toolTipRight}
            />
            <Content
                hovered={false}
                userSelectable={true}
                empty={false}
                focused={focused || false}
                icon={null}
                context={props.context}
            >
                {renderInput()}
                {renderSaveCancelIcons()}
            </Content>
            <BelowContent
                focused={focused || false}
                hovered={false}
                underline={true}
                errorText={props.errorText}
                context={props.context}
            />
        </LabeledElement>
    );
}
